import rawDirectory from "@/lib/utilities/utilities.json";
import { MAP_CITIES, TDU_DISPLAY, TDU_CODES, NON_CHOICE_CODES } from "@/lib/texasTduMap";

/**
 * Texas utility directory model. The data file (content/utilities/utilities.json, copied to
 * src/lib/utilities/utilities.json by scripts/build-utilities-data.js) is the ONLY source of phone numbers,
 * outage links, hours and FAQs on the site; nothing here invents a value.
 *
 * Display rules enforced in this file, once, so the directory page and the map panel can never disagree:
 *  - A phone number is shown only when it has a usable number AND a confidence value. Missing confidence = hidden.
 *  - A verified date is shown only when verified_on parses; otherwise no "verified" text at all.
 *  - Every shown number gets a tel: href built from its digits.
 *  - Missing optional fields (hours, website, outage links, FAQs, whole phone list) degrade to nothing, never to
 *    "undefined" or an empty box.
 */

const str = (v) => (typeof v === "string" ? v.trim() : "");

/** A tel: href for a US number in any common format (dashes, dots, parentheses, a leading 1 or +1, a trailing extension) or a 3-digit
 * service code (311, 511, 711, 911). Anything else returns null: we do not offer a tap-to-call link we cannot stand behind.
 * A number must also be a possible North American number (area code and exchange start with 2-9), so a typo with a leading
 * zero or one in the data file is hidden instead of becoming a dead tap-to-call link. */
export function telHref(number) {
  const main = str(number).split(/\s*(?:ext(?:ension)?\.?|x|#)\s*(?=\d)/i)[0];
  const digits = main.replace(/\D/g, "");
  const national = digits.length === 11 && digits[0] === "1" ? digits.slice(1) : digits;
  if (national.length === 10 && /^[2-9]\d{2}[2-9]\d{6}$/.test(national)) return `tel:+1${national}`;
  if (digits.length === 3 && /^[2-9]11$/.test(digits)) return `tel:${digits}`;
  return null;
}

const hasConfidence = (c) => {
  if (c === null || c === undefined) return false;
  if (typeof c === "number") return Number.isFinite(c) && c > 0;
  return str(String(c)) !== "";
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-12" -> "Oct 12, 2026". Parsed by hand (no Date) so the day never shifts with the viewer's timezone. Invalid -> null. */
export function formatVerified(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str(iso));
  if (!m) return null;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${MONTHS[month - 1]} ${day}, ${m[1]}`;
}

/** Order on a card: the time-critical numbers first. */
const KIND_ORDER = { outage: 0, emergency: 1, customer: 2, other: 3 };

export function phoneKind(label) {
  const l = str(label).toLowerCase();
  if (/outage|power-out|power out|no power|trouble/.test(l)) return "outage";
  if (/emergenc|downed|hazard|danger|line down|wire/.test(l)) return "emergency";
  if (/customer|general|billing|service|account|office|inquir/.test(l)) return "customer";
  return "other";
}

/** The data file's labels carry detail in parentheses or after a semicolon; the narrow map panel shows only the lead phrase. */
export function shortLabel(label) {
  const lead = str(label).split(/\s*[(;]/)[0].trim();
  return lead || str(label);
}

const SAFETY_WORDS = /emergenc|downed|hazard|danger|911/i;

/** A source URL shown as its bare host ("oncor.com"). Non-http(s) or unparseable URLs are dropped. Several URLs on the same host
 * show one link (the first), so a row never reads "epelectric.com, epelectric.com". */
export function sourceLinks(urls) {
  const seenHosts = new Set();
  return (Array.isArray(urls) ? urls : [])
    .map((u) => {
      try {
        const url = new URL(str(u));
        if (url.protocol !== "https:" && url.protocol !== "http:") return null;
        return { href: url.href, host: url.hostname.replace(/^www\./, "") };
      } catch (e) {
        return null;
      }
    })
    .filter(Boolean)
    .filter((l) => (seenHosts.has(l.host) ? false : (seenHosts.add(l.host), true)));
}

export function usablePhones(utility) {
  const list = Array.isArray(utility && utility.phones) ? utility.phones : [];
  return list
    .map((p, index) => {
      if (!p || typeof p !== "object") return null;
      const number = str(p.number);
      const href = telHref(number);
      const label = str(p.label);
      if (!number || !href || !label || !hasConfidence(p.confidence)) return null;
      const kind = phoneKind(label);
      return {
        key: `${index}-${href}`,
        label,
        shortLabel: shortLabel(label),
        number,
        href,
        hours: str(p.hours) || null,
        kind,
        // Any number whose label is about an emergency, downed line, hazard, or 911 carries the 911 reminder.
        call911First: SAFETY_WORDS.test(label) && href !== "tel:911",
        verified: formatVerified(p.verified_on),
        verifiedIso: formatVerified(p.verified_on) ? str(p.verified_on).slice(0, 10) : null,
        lowConfidence: typeof p.confidence === "string" && p.confidence.trim().toLowerCase() === "low",
        sources: sourceLinks(p.source_urls),
        order: index,
      };
    })
    .filter(Boolean)
    .sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.order - b.order);
}

/** One verified-on label for a card: the most recent date among the shown numbers. */
export function latestVerified(phones) {
  const iso = (Array.isArray(phones) ? phones : []).map((p) => p.verifiedIso).filter(Boolean).sort();
  return iso.length ? formatVerified(iso[iso.length - 1]) : null;
}

const validHttp = (u) => {
  try {
    const url = new URL(str(u));
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch (e) {
    return null;
  }
};

const cleanFaq = (list) =>
  (Array.isArray(list) ? list : [])
    .map((f) => ({ q: str(f && f.q), a: str(f && f.a), sources: sourceLinks(f && f.source_urls) }))
    .filter((f) => f.q && f.a);

export function normalizeUtility(u) {
  const phones = usablePhones(u);
  return {
    code: str(u.code),
    name: str(u.name),
    shortName: str(u.short_name) || str(u.name),
    type: str(u.type),
    serviceArea: str(u.service_area),
    website: validHttp(u.website),
    outageUrl: validHttp(u.outage_url),
    outageMapUrl: validHttp(u.outage_map_url),
    phones,
    verified: latestVerified(phones),
    faq: cleanFaq(u.faq),
  };
}

/** Raw JSON -> display model. Tolerates a missing/empty/partial file: the result is just a shorter (possibly empty) directory. */
/** The same question asked on two pages with DIFFERENT answers (for example "What if I see a downed power line?" for two
 * utilities) is made unambiguous by adding the utility's short name to the question. This runs once, here, so the visible
 * accordion and the FAQPage markup always carry the identical string. */
function disambiguateFaq(utilities, sharedFaq) {
  const answers = new Map();
  const note = (q, a) => answers.set(q, (answers.get(q) || new Set()).add(a));
  utilities.forEach((u) => u.faq.forEach((f) => note(f.q, f.a)));
  sharedFaq.forEach((f) => note(f.q, f.a));
  return utilities.map((u) => ({
    ...u,
    faq: u.faq.map((f) => (answers.get(f.q).size > 1 ? { ...f, q: `${f.q} (${u.shortName})` } : f)),
  }));
}

export function normalizeDirectory(raw) {
  const list = Array.isArray(raw && raw.utilities) ? raw.utilities : [];
  const sharedFaq = cleanFaq(raw && raw.shared_faq);
  const utilities = disambiguateFaq(list.filter((u) => u && str(u.code) && str(u.name)).map(normalizeUtility), sharedFaq);
  return {
    updated: formatVerified(raw && raw.updated),
    utilities,
    byCode: Object.fromEntries(utilities.map((u) => [u.code, u])),
    sharedFaq,
  };
}

export const DIRECTORY = normalizeDirectory(rawDirectory);

export const cardId = (code) => `utility-${code}`;

/** Directory order: retail-choice utilities, then regulated ones (the map's own order), then anything else in the file. */
export function orderedGroups(directory) {
  const take = (codes) => codes.map((c) => directory.byCode[c]).filter(Boolean);
  const known = new Set([...TDU_CODES, ...NON_CHOICE_CODES]);
  return {
    choice: take(TDU_CODES),
    regulated: take(NON_CHOICE_CODES),
    other: directory.utilities.filter((u) => !known.has(u.code)),
  };
}

/** The ONE list of FAQ items, in on-page order, used for both the visible accordions and the FAQPage JSON-LD. Identical
 * question+answer pairs (the same shared answer on two utilities) are listed once so the markup has no duplicates. */
export function allFaqItems(directory) {
  const seen = new Set();
  const out = [];
  const add = (f) => {
    const key = `${f.q}\u0000${f.a}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ question: f.q, answer: f.a });
  };
  const groups = orderedGroups(directory);
  [...groups.choice, ...groups.regulated, ...groups.other].forEach((u) => u.faq.forEach(add));
  directory.sharedFaq.forEach(add);
  return out;
}

/* ----------------------------- Find my utility ----------------------------- */

// The three map reference cities whose utility is not in the 6-code choice set. Austin / San Antonio / El Paso are
// served by the municipal or regulated utilities on the map (texasTduMap.js MAP_CITIES documents tdu:null for them).
const NON_CHOICE_CITY = { austin: "austin_energy", "san antonio": "cps_energy", "el paso": "el_paso_electric" };
// Cities named in the Entergy Texas coverage note shown on the map panel (texasTduMap.js NON_CHOICE_DISPLAY).
const ENTERGY_CITIES = ["Conroe", "The Woodlands", "Beaumont", "Lufkin", "Nacogdoches"];
// TNMP's service area is scattered pockets, so a city name alone cannot say which utility serves an address. Its listed
// cities are left out of the city helper on purpose; those searches go to the exact-address ESID lookup.

const normCity = (s) =>
  str(s)
    .toLowerCase()
    .replace(/[.,]/g, " ")
    .replace(/\s+(tx|texas)\s*$/, "")
    .replace(/\s+/g, " ")
    .trim();

function buildCityIndex() {
  const index = new Map();
  const put = (name, code) => {
    const k = normCity(name);
    if (k && !index.has(k)) index.set(k, { name, code });
  };
  MAP_CITIES.forEach((c) => {
    if (c.tdu) put(c.name, c.tdu);
    else if (NON_CHOICE_CITY[normCity(c.name)]) put(c.name, NON_CHOICE_CITY[normCity(c.name)]);
  });
  TDU_CODES.filter((code) => code !== "tnmp").forEach((code) => TDU_DISPLAY[code].majorCities.forEach((n) => put(n, code)));
  ENTERGY_CITIES.forEach((n) => put(n, "entergy_texas"));
  return index;
}

const CITY_INDEX = buildCityIndex();

export const CITY_SUGGESTIONS = Array.from(CITY_INDEX.values())
  .map((c) => c.name)
  .sort((a, b) => a.localeCompare(b));

/** Result kinds: "empty" (nothing typed), "zip" (ZIP codes cannot be mapped to one utility), "match", "none". */
export function findUtilityForQuery(query) {
  const q = str(query);
  if (!q) return { kind: "empty" };
  if (/^\d{5}(-\d{4})?$/.test(q)) return { kind: "zip", query: q };
  const hit = CITY_INDEX.get(normCity(q));
  if (!hit) return { kind: "none", query: q };
  return { kind: "match", city: hit.name, code: hit.code };
}
